// C:\Projects\texe-web\src\hooks\useFeedMeta.ts
import { useQuery } from "@tanstack/react-query";
import {
  fetchBatchLikeState,
  fetchBatchFollowStatus,
  fetchBatchBlockStatus,
  type LikeState,
  type FollowStatus,
  type BlockStatus,
} from "../services/batch";

export type FeedMetaMaps = {
  likesByPost: Map<string, LikeState>;
  likesByComment: Map<string, LikeState>;
  followByUser: Map<string, FollowStatus>;
  blockByUser: Map<string, BlockStatus>;
};

/** Feed (veya liste) sayfalarında id kümeleriyle çağır: tek seferde toplar. */
export function useFeedMeta(params: {
  postIds?: string[];
  commentIds?: string[];
  authorIds?: string[];
  enabled?: boolean;
}) {
  const postIds = (params.postIds ?? []).filter(Boolean);
  const commentIds = (params.commentIds ?? []).filter(Boolean);
  const authorIds = (params.authorIds ?? []).filter(Boolean);
  const enabled = params.enabled ?? true;

  return useQuery({
    queryKey: [
      "feed-meta",
      postIds.slice().sort().join(","),
      commentIds.slice().sort().join(","),
      authorIds.slice().sort().join(","),
    ],
    queryFn: async (): Promise<FeedMetaMaps> => {
      const [likes, follows, blocks] = await Promise.all([
        fetchBatchLikeState({ postIds, commentIds }),
        fetchBatchFollowStatus(authorIds),
        fetchBatchBlockStatus(authorIds),
      ]);

      const likesByPost = new Map<string, LikeState>();
      const likesByComment = new Map<string, LikeState>();
      for (const p of likes.posts) likesByPost.set(p.id, p);
      for (const c of likes.comments) likesByComment.set(c.id, c);

      const followByUser = new Map<string, FollowStatus>();
      for (const f of follows) followByUser.set(f.id, f);

      const blockByUser = new Map<string, BlockStatus>();
      for (const b of blocks) blockByUser.set(b.id, b);

      return { likesByPost, likesByComment, followByUser, blockByUser };
    },
    enabled: enabled && (postIds.length + commentIds.length + authorIds.length) > 0,
    staleTime: 5_000, // backend TTL ile uyumlu
    gcTime: 60_000,
  });
}
